import { CircleHelpIcon } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SlotSheetRow } from './slot-sheet-section';

/**
 * Definition-list row holding a single toggle. The explanation lives in a
 * tooltip rather than under the label, so the row keeps the one-line rhythm of
 * the rest of the list.
 *
 * @param props.label - the row label
 * @param props.description - the explanation shown in the tooltip
 * @param props.checked - whether the option is on
 * @param props.disabled - whether the viewer may change it
 * @param props.onCheckedChange - called with the new value
 * @returns the row's label and value elements
 */
export const SlotSheetCheckboxRow = ({
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) => (
  <SlotSheetRow
    label={
      <span className="flex items-center gap-1.5">
        {label}
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="inline-flex"
              role="img"
              aria-label={description}
            >
              <CircleHelpIcon
                className="size-3.5 text-muted-foreground/70"
                aria-hidden="true"
              />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-64">{description}</TooltipContent>
        </Tooltip>
      </span>
    }
  >
    <Checkbox
      checked={checked}
      disabled={disabled}
      onCheckedChange={(value) => onCheckedChange(value === true)}
      aria-label={label}
    />
  </SlotSheetRow>
);
